import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePermission } from '@/hooks/usePermission'
import { motion, useReducedMotion, type Variants } from 'framer-motion'
import {
  UsersThree,
  PaperPlaneTilt,
  Plus,
  TreeStructure,
  FileText,
  Sparkle,
  ArrowsClockwise,
  UploadSimple,
  Warning,
  WarningCircle,
  GearSix,
  Target,
  Lightning,
  ArrowRight,
  CheckCircle,
  TrendUp,
  Buildings,
  Lightbulb,
  Rocket,
  type Icon as PhosphorIconType,
} from '@phosphor-icons/react'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { analyticsApi, materialsApi, onboardingApi, getErrorMessage } from '@/lib/api'
import { sanitizeInsightSummary } from '@/lib/insights'
import { statusToBadge, type BadgeVariant } from '@/lib/utils'
import { campaignStatusMeta } from '@/lib/campaignStatus'
import { totalRemainingCredits, creditsGranted } from '@/lib/billingFormat'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { useBillingStore } from '../../stores/billingStore'
import ContentInsightsModal from './ContentInsightsModal'
import LaunchCampaignModal from '../campaigns/LaunchCampaignModal'
import EmailPerformanceFunnel from './components/EmailPerformanceFunnel'
import { DotLoader } from '@/components/ui/DotLoader'
import { Skeleton } from '@/components/ui/Skeleton'
import { TextShimmer } from '@/components/ui/TextShimmer'
import {
  NextStepHero,
  SetupChecklist,
  SupportStrip,
  ProfileCompletionCard,
} from './onboarding/OnboardingPanels'
import {
  dismissProfileCard,
  isProfileCardDismissed,
  profileCardMode,
  showDashboardSections,
  showOnboardingPanel,
  type OnboardingStatus,
} from './onboarding/onboardingLogic'

// Editorial Dark palette — dual-mode CSS custom properties scoped to dashboard page.
const darkPalette: React.CSSProperties = {
  '--ed-canvas': '#08090a',
  '--ed-panel': '#0f1011',
  '--ed-elev-1': '#141516',
  '--ed-elev-2': '#1a1b1e',
  '--ed-text-1': '#f7f8f8',
  '--ed-text-2': '#d0d6e0',
  '--ed-text-3': '#8a8f98',
  '--ed-text-4': '#62666d',
  '--ed-text-5': '#3e3e44',
  '--ed-border': 'rgba(255,255,255,0.08)',
  '--ed-border-subtle': 'rgba(255,255,255,0.05)',
  '--ed-border-strong': 'rgba(255,255,255,0.14)',
  '--ed-bg-idle': 'rgba(255,255,255,0.02)',
  '--ed-bg-hover': 'rgba(255,255,255,0.04)',
  '--ed-bg-nested': 'rgba(255,255,255,0.02)',
  '--ed-bg-track': 'rgba(255,255,255,0.05)',
  '--ed-bg-track-soft': 'rgba(255,255,255,0.025)',
  '--ed-brand': '#5e6ad2',
  '--ed-accent': '#7170ff',
  '--ed-accent-hover': '#828fff',
  '--ed-accent-pressed': '#5058c0',
  '--ed-accent-soft': 'rgba(113,112,255,0.10)',
  '--ed-accent-border': 'rgba(113,112,255,0.25)',
  '--ed-success': '#27a644',
  '--ed-success-soft': 'rgba(39,166,68,0.10)',
  '--ed-warn-icon': '#f5a524',
  '--ed-warn-text': '#a3741f',
  '--ed-warn-soft-1': 'rgba(163,116,31,0.08)',
  '--ed-warn-soft-2': 'rgba(163,116,31,0.12)',
  '--ed-warn-border': 'rgba(163,116,31,0.25)',
  '--ed-danger': '#e2545b',
  '--ed-danger-soft': 'rgba(226,84,91,0.10)',
  '--ed-danger-border': 'rgba(226,84,91,0.25)',
  '--ed-dot-grid': 'rgba(255,255,255,0.035)',
  '--ed-mesh-1': 'rgba(94,106,210,0.06)',
  '--ed-mesh-2': 'rgba(113,112,255,0.05)',
} as React.CSSProperties

const lightPalette: React.CSSProperties = {
  // Must stay byte-identical to `--background` in index.css (0 0% 98% = #fafafa).
  // This page paints its own canvas edge-to-edge under a transparent top bar, so
  // any mismatch shows as a colour band across the top. It was hsl(30 25% 98%) —
  // warm hue 30 against an achromatic shell.
  '--ed-canvas': '#fafafa',
  '--ed-panel': '#ffffff',
  '--ed-elev-1': '#f0f1f3',
  '--ed-elev-2': '#e8eaee',
  '--ed-text-1': '#0f1011',
  '--ed-text-2': '#3e3e44',
  '--ed-text-3': '#5b6068',
  '--ed-text-4': '#8a8f98',
  '--ed-text-5': '#c0c4cb',
  '--ed-border': 'rgba(15,16,17,0.10)',
  '--ed-border-subtle': 'rgba(15,16,17,0.06)',
  '--ed-border-strong': 'rgba(15,16,17,0.18)',
  '--ed-bg-idle': '#ffffff',
  '--ed-bg-hover': '#f5f6f9',
  '--ed-bg-nested': '#f5f6f9',
  '--ed-bg-track': 'rgba(15,16,17,0.06)',
  '--ed-bg-track-soft': 'rgba(15,16,17,0.025)',
  '--ed-brand': '#5e6ad2',
  '--ed-accent': '#5e6ad2',
  '--ed-accent-hover': '#7170ff',
  '--ed-accent-pressed': '#4651b8',
  '--ed-accent-soft': 'rgba(94,106,210,0.10)',
  '--ed-accent-border': 'rgba(94,106,210,0.25)',
  '--ed-success': '#1f8a3a',
  '--ed-success-soft': 'rgba(31,138,58,0.10)',
  '--ed-warn-icon': '#b6741a',
  '--ed-warn-text': '#7a571a',
  '--ed-warn-soft-1': 'rgba(184,116,26,0.10)',
  '--ed-warn-soft-2': 'rgba(184,116,26,0.14)',
  '--ed-warn-border': 'rgba(184,116,26,0.30)',
  '--ed-danger': '#c33a40',
  '--ed-danger-soft': 'rgba(195,58,64,0.10)',
  '--ed-danger-border': 'rgba(195,58,64,0.25)',
  '--ed-dot-grid': 'rgba(15,16,17,0.045)',
  '--ed-mesh-1': 'rgba(94,106,210,0.05)',
  '--ed-mesh-2': 'rgba(113,112,255,0.04)',
} as React.CSSProperties

interface MarketingStrategy {
  title: string
  description: string
  priority: 'high' | 'medium' | 'low'
  estimated_impact?: string
  metrics_to_track?: string[]
}

interface TargetIndustry {
  industry: string
  fit_score: number
  reasoning: string
  market_size_indicator?: string
  approach?: string
}

interface IdealICP {
  job_titles?: string[]
  company_size?: string
  pain_points?: string[]
  buying_triggers?: string[]
  decision_criteria?: string[]
  industries?: string[]
  objections?: string[]
  messaging_angles?: string[]
}

interface AdditionalInsight {
  category: string
  insight: string
  actionable_recommendation: string
}

interface ContentGap {
  gap: string
  impact: string
  recommendation: string
}

interface CampaignSuggestion {
  name: string
  objective: string
  target_audience: string
  key_message: string
  channels?: string[]
  expected_outcome?: string
}

interface CompetitivePositioning {
  summary?: string
  differentiators?: string[]
  value_propositions?: string[]
}

interface InsightsData {
  status: 'completed' | 'not_generated' | 'failed' | 'no_materials'
  generated_at?: string
  is_stale?: boolean
  material_count?: number
  executive_summary?: string
  marketing_strategies?: MarketingStrategy[]
  target_industries?: TargetIndustry[]
  ideal_icp?: IdealICP
  additional_insights?: AdditionalInsight[]
  next_steps?: string[]
  content_gaps?: ContentGap[]
  competitive_positioning?: CompetitivePositioning
  campaign_suggestions?: CampaignSuggestion[]
}

interface DashboardData {
  overview: {
    total_contacts: number
    active_contacts: number
    total_campaigns: number
    active_campaigns: number
    total_workflows: number
    active_workflows: number
    total_materials: number
  }
  email_performance: {
    total_sent: number
    total_delivered: number
    total_opened: number
    total_clicked: number
    total_replied: number
    total_positive_replied: number
    total_bounced: number
    avg_open_rate: number
    avg_click_rate: number
    avg_reply_rate: number
  }
  campaigns: {
    total: number
    scheduled: number
    running: number
    completed: number
    paused: number
    failed: number
  }
  workflows: {
    total: number
    active: number
    paused: number
    draft: number
  }
  contacts: {
    total: number
    by_status: Record<string, number>
    by_email_status: Record<string, number>
  }
  recent_campaigns: {
    id: number
    name: string
    status: string
    total_recipients: number
    emails_sent: number
    emails_opened?: number
    open_rate: number
    created_at: string | null
  }[]
}

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const stageVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

// Sheen sweep for the primary CTAs. Driven by variant labels that the parent
// button propagates on hover/focus — it used to run `repeat: Infinity`, i.e. a
// decorative animation looping for the entire life of the page on the loudest
// control on the screen, keeping a compositor layer permanently alive.
const sheenVariants: Variants = {
  rest: { x: '-40%', transition: { duration: 0 } },
  sweep: { x: '140%', transition: { duration: 0.9, ease: 'easeInOut' } },
}

const ButtonSheen: React.FC = () => {
  // Not rendered at all under prefers-reduced-motion. The previous
  // `motion-reduce:hidden` only hid the element — framer-motion kept animating
  // it, because a class-based media query is invisible to the motion values.
  const reduced = useReducedMotion()
  if (reduced) return null
  return (
    <motion.span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      variants={sheenVariants}
    >
      <span
        className="absolute top-0 bottom-0 bg-white/20"
        style={{ left: 0, width: '18px', transform: 'skewX(-20deg)' }}
      />
      <span
        className="absolute top-0 bottom-0 bg-white/15"
        style={{ left: '22px', width: '5px', transform: 'skewX(-20deg)' }}
      />
    </motion.span>
  )
}

// Campaign status colour derives from `statusToBadge` in lib/utils — the single
// status vocabulary (§60) — instead of a private map. The dashboard's own map
// disagreed with it on two states: `completed` painted emerald where the shared
// source then said indigo, and `draft` painted amber where it says default.
// The §60 ruling has since settled `completed` on emerald and moved `running` to
// indigo, and this surface inherits both by reading the shared tone.
const badgeVariantToTint: Record<BadgeVariant, string> = {
  default: 'var(--ed-text-4)',
  success: 'var(--ed-success)',
  warning: 'var(--ed-warn-icon)',
  destructive: 'var(--ed-danger)',
  info: 'var(--ed-accent)',
}

const editorialFont: React.CSSProperties = {
  fontFamily: "'Inter', -apple-system, system-ui, 'Segoe UI', Roboto, sans-serif",
  fontFeatureSettings: '"cv01", "ss03"',
}

const serifFont: React.CSSProperties = {
  fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
  fontWeight: 400,
}

const monoFont: React.CSSProperties = {
  fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  fontWeight: 400,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
}

const meshBackground =
  'radial-gradient(at 14% 18%, var(--ed-mesh-1) 0px, transparent 50%),' +
  'radial-gradient(at 86% 80%, var(--ed-mesh-2) 0px, transparent 55%)'

// Section caption — Berkeley Mono editorial label
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p
    className="text-[var(--ed-text-4)] mb-3"
    style={{ ...monoFont, fontSize: '10px' }}
  >
    {children}
  </p>
)

// Editorial dark card primitive
const DarkCard: React.FC<{
  children: React.ReactNode
  className?: string
  onClick?: () => void
  hoverable?: boolean
}> = ({ children, className = '', onClick, hoverable = true }) => {
  const Component = motion.div
  const isInteractive = hoverable && !!onClick
  return (
    <Component
      onClick={onClick}
      className={`relative rounded-lg bg-[var(--ed-bg-idle)] border border-[var(--ed-border)] ${
        onClick ? 'cursor-pointer' : ''
      } ${
        isInteractive ? 'hover:bg-[var(--ed-bg-hover)] hover:border-[var(--ed-accent-border)]' : ''
      } ${className}`}
      whileHover={isInteractive ? { y: -2, transition: { duration: 0.2, ease: easeOutExpo } } : undefined}
    >
      {children}
    </Component>
  )
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { actualTheme } = useThemeStore()
  const isDark = actualTheme === 'dark'
  const palette = isDark ? darkPalette : lightPalette
  const { credits, creditsLoaded, fetchCredits } = useBillingStore()
  // Whether this role has a Billing page to land on at all -- the same two
  // cells the nav item is gated on (components/layouts/DashboardLayout).
  // Without this the credits card sent a Rep or Manager to a page whose
  // only content is a notice that billing is not theirs; the card still
  // shows them their balance, it just stops being a link.
  const canManageBilling = usePermission('billing', 'manage_subscription').allowed
  const creditUsagePerm = usePermission('credit_budgets', 'view_usage')
  const canOpenBilling = canManageBilling || (creditUsagePerm.allowed && creditUsagePerm.scope !== 'own')
  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [dataError, setDataError] = useState<string | null>(null)
  const [insights, setInsights] = useState<InsightsData | null>(null)
  const [insightsLoading, setInsightsLoading] = useState(false)
  const [insightsGenerating, setInsightsGenerating] = useState(false)
  const [insightsError, setInsightsError] = useState<string | null>(null)
  const [showInsightsModal, setShowInsightsModal] = useState(false)
  const [showLaunchModal, setShowLaunchModal] = useState(false)
  // One status feed drives the home state, checklist, hero and profile card.
  // A failed fetch is non-fatal: we fall back to the plain dashboard.
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null)
  const [profileDismissed, setProfileDismissed] = useState(() => isProfileCardDismissed(user?.id))

  useEffect(() => {
    fetchCredits()
  }, [fetchCredits])

  const loadOnboarding = useCallback(() => {
    onboardingApi
      .getStatus()
      .then((res) => setOnboarding(res.data))
      .catch(() => console.warn('Failed to load onboarding status'))
  }, [])

  useEffect(() => {
    loadOnboarding()
  }, [loadOnboarding])

  // A failed fetch used to toast once and fall through to the zero-value
  // defaults below, so the dashboard rendered "0 contacts / 0.0% open rate"
  // behind a dismissed toast — a fetch failure read as a real, empty account.
  // `dataError` now drives a terminal error state with a retry.
  const loadDashboard = useCallback((options?: { silent?: boolean }) => {
    setIsLoading(true)
    setDataError(null)
    analyticsApi
      .getDashboard()
      .then((res) => {
        setData(res.data)
        setDataError(null)
      })
      .catch((err: unknown) => {
        const message = getErrorMessage(err, 'Failed to load dashboard data')
        setData(null)
        setDataError(message)
        if (!options?.silent) toast.error(message)
      })
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    loadDashboard()

    setInsightsLoading(true)
    materialsApi
      .getInsights()
      .then((res) => setInsights(res.data))
      .catch(() => {
        console.warn('Failed to load cached insights')
      })
      .finally(() => setInsightsLoading(false))
  }, [loadDashboard])

  const handleGenerateInsights = async () => {
    setInsightsGenerating(true)
    setInsightsError(null)
    try {
      const res = await materialsApi.generateInsights()
      setInsights(res.data)
    } catch (err: unknown) {
      setInsightsError(getErrorMessage(err, 'Failed to generate insights'))
    } finally {
      setInsightsGenerating(false)
    }
  }

  const fmt = (n: number) => n.toLocaleString()
  const pct = (n: number) => `${n.toFixed(1)}%`

  const o = data?.overview || {
    total_contacts: 0,
    active_contacts: 0,
    total_campaigns: 0,
    active_campaigns: 0,
    total_workflows: 0,
    active_workflows: 0,
    total_materials: 0,
  }
  const ep = data?.email_performance || {
    total_sent: 0,
    total_delivered: 0,
    total_opened: 0,
    total_clicked: 0,
    total_replied: 0,
    total_positive_replied: 0,
    total_bounced: 0,
    avg_open_rate: 0,
    avg_click_rate: 0,
    avg_reply_rate: 0,
  }
  const cs = data?.contacts || { total: 0, by_status: {}, by_email_status: {} }

  // Spendable = plan remaining + purchased extra-credit wallet. Counting only
  // the plan bucket here showed "0 credits / critical" to users who had burnt
  // their plan quota but were sitting on thousands of purchased extra credits.
  const spendableCredits = totalRemainingCredits(credits)
  const totalCredits = creditsGranted(credits)
  // No allowance is not the same as a full allowance. The fallback used to be
  // 100, so an account with zero credits rendered "0" above "100% remaining".
  const hasAllowance = !!credits && totalCredits > 0
  const remainingPct = hasAllowance
    ? Math.round((spendableCredits / totalCredits) * 100)
    : 0
  const creditSeverity: 'critical' | 'warn' | 'ok' = !hasAllowance
    ? 'ok'
    : remainingPct <= 5
      ? 'critical'
      : remainingPct <= 20
        ? 'warn'
        : 'ok'

  const firstName = user?.full_name?.split(' ')[0] || 'there'
  const isNewHome = onboarding?.home_state === 'new'
  // No status yet (loading or failed) keeps the original dashboard.
  const showDash = onboarding ? showDashboardSections(onboarding) : true
  const cardMode = onboarding ? profileCardMode(onboarding, profileDismissed) : 'hidden'

  if (isLoading) {
    return (
      <div
        className="relative -mx-6 sm:-mx-8 lg:-mx-10 -mt-6 -mb-8 px-6 sm:px-8 lg:px-10 pt-10 pb-16 min-h-screen overflow-hidden flex items-center justify-center"
        style={{ ...editorialFont, ...palette, background: 'var(--ed-canvas)' }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: meshBackground }}
        />
        <div className="relative flex flex-col items-center gap-4">
          <DotLoader size="md" className="text-[var(--ed-accent)]" />
          <p
            className="text-[var(--ed-text-4)]"
            style={{ ...monoFont, fontSize: '10px' }}
          >
            Loading dashboard
          </p>
        </div>
      </div>
    )
  }

  // The whole page is derived from one payload, so there is nothing honest to
  // render without it. §24: an empty screen is an invitation to act.
  if (dataError) {
    return (
      <div
        className="relative -mx-6 sm:-mx-8 lg:-mx-10 -mt-6 -mb-8 px-6 sm:px-8 lg:px-10 pt-10 pb-16 min-h-screen overflow-hidden flex items-center justify-center"
        style={{ ...editorialFont, ...palette, background: 'var(--ed-canvas)' }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: meshBackground }}
        />
        <div
          role="alert"
          className="relative flex flex-col items-center text-center max-w-sm"
        >
          <div className="h-11 w-11 rounded-md bg-[var(--ed-danger-soft)] border border-[var(--ed-danger-border)] flex items-center justify-center mb-4">
            <WarningCircle className="h-5 w-5 text-[var(--ed-danger)]" weight="regular" />
          </div>
          <p
            className="text-[var(--ed-text-1)] mb-1.5"
            style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
          >
            Couldn't load your dashboard
          </p>
          <p
            className="text-[var(--ed-text-3)] mb-5"
            style={{ fontSize: '13px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
          >
            {dataError}
          </p>
          <SecondaryButton
            icon={ArrowsClockwise}
            label="Try again"
            onClick={() => loadDashboard({ silent: true })}
          />
        </div>
      </div>
    )
  }

  const statusPillStyle: React.CSSProperties = {
    fontSize: '10px',
    fontWeight: 510,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  }

  // Single source of truth: `statusToBadge` (lib/utils). Chip tint is derived
  // from the shared badge variant so one concept cannot carry two colours.
  const getStatusTint = (status: string): { dot: string; text: string } => {
    const variant = statusToBadge[status?.toLowerCase()] || 'default'
    return { dot: badgeVariantToTint[variant], text: 'var(--ed-text-2)' }
  }

  // Raw backend enums are not user-visible copy.

  return (
    <div
      className="relative -mx-6 sm:-mx-8 lg:-mx-10 -mt-6 -mb-8 px-6 sm:px-8 lg:px-10 pt-6 pb-10 min-h-screen overflow-hidden"
      style={{ ...editorialFont, ...palette, background: 'var(--ed-canvas)' }}
    >
      {/* Atmosphere — gradient mesh */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: meshBackground }}
      />

      {/* Atmosphere — dot grid */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          backgroundImage:
            'radial-gradient(circle, var(--ed-dot-grid) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      <motion.div
        className="relative max-w-[1400px] mx-auto space-y-8"
        variants={stageVariants}
        initial="hidden"
        animate="show"
      >
        {/* Hero */}
        <motion.header
          className="flex items-end justify-between gap-6 flex-wrap"
          variants={itemVariants}
        >
          <div className="flex-1 min-w-0">
            <h1
              className="text-[var(--ed-text-1)]"
              style={{
                ...serifFont,
                fontSize: 'clamp(36px, 4vw, 48px)',
                lineHeight: 1.05,
                letterSpacing: '-0.8px',
              }}
            >
              {isNewHome ? 'Welcome to Beevelope,' : 'Welcome back,'}{' '}
              <span className="text-[var(--ed-accent)]" style={{ fontStyle: 'italic' }}>
                {firstName}
              </span>
            </h1>
            <p
              className="text-[var(--ed-text-3)] mt-2"
              style={{ fontSize: '15px', lineHeight: 1.6, letterSpacing: '-0.165px' }}
            >
              {isNewHome
                ? "Let's get your first campaign out the door."
                : "Here's what's happening across your campaigns today."}
            </p>
          </div>
          <motion.button
            onClick={() => setShowLaunchModal(true)}
            className="group relative overflow-hidden inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[var(--ed-brand)] hover:bg-[var(--ed-accent-hover)] active:bg-[var(--ed-accent-pressed)] transition-colors duration-200 text-white"
            style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
            initial="rest"
            animate="rest"
            whileHover="sweep"
            whileFocus="sweep"
            whileTap={{ scale: 0.97 }}
          >
            <ButtonSheen />
            <Plus className="relative h-4 w-4" weight="bold" />
            <span className="relative">New Campaign</span>
          </motion.button>
        </motion.header>

        {/* Onboarding — hero, checklist, support (new / in-progress) */}
        {onboarding && showOnboardingPanel(onboarding) && (
          <motion.section variants={itemVariants}>
            <SectionLabel>Get started</SectionLabel>
            <div className="space-y-4">
            <div className={isNewHome ? 'grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4' : ''}>
              <NextStepHero
                status={onboarding}
                firstName={firstName}
                onGo={(route) => navigate(route)}
                onLaunch={() => setShowLaunchModal(true)}
              />
              {isNewHome && (
                <CreditCard
                  remaining={credits ? spendableCredits : undefined}
                  remainingPct={remainingPct}
                  hasAllowance={hasAllowance}
                  loaded={creditsLoaded}
                  severity={creditSeverity}
                  onClick={canOpenBilling ? () => navigate('/billing') : undefined}
                />
              )}
            </div>
            <SetupChecklist
              status={onboarding}
              onGo={(route) => navigate(route)}
              onLaunch={() => setShowLaunchModal(true)}
            />
            <SupportStrip />
            </div>
          </motion.section>
        )}

        {showDash && (
        <>
        {/* Overview — KPI strip */}
        <motion.section variants={itemVariants}>
          <SectionLabel>Overview</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Total Contacts"
              value={fmt(o.total_contacts)}
              meta={`${fmt(o.active_contacts)} active`}
              icon={UsersThree}
              onClick={() => navigate('/contacts')}
            />
            <KpiCard
              label="Active Campaigns"
              value={fmt(o.active_campaigns)}
              meta={`${fmt(o.total_campaigns)} total`}
              icon={PaperPlaneTilt}
              onClick={() => navigate('/campaigns')}
            />
            <KpiCard
              label="Avg Open Rate"
              value={pct(ep.avg_open_rate)}
              meta={`${fmt(ep.total_sent)} sent · all time`}
              icon={Target}
              onClick={() => navigate('/analytics')}
            />
            <CreditCard
              remaining={credits ? spendableCredits : undefined}
              remainingPct={remainingPct}
              hasAllowance={hasAllowance}
              loaded={creditsLoaded}
              severity={creditSeverity}
              onClick={canOpenBilling ? () => navigate('/billing') : undefined}
            />
          </div>
        </motion.section>

        {/* Activity — Email Performance + Contact Pipeline */}
        <motion.section variants={itemVariants}>
          <SectionLabel>Activity</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Email Performance — vertical funnel */}
            <DarkCard hoverable={false} className="p-5">
              <EmailPerformanceFunnel ep={ep} />
            </DarkCard>

            {/* Contact Pipeline */}
            <DarkCard hoverable={false} className="p-5 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2
                  className="text-[var(--ed-text-1)]"
                  style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
                >
                  Contact Pipeline
                </h2>
                <span className="text-[var(--ed-text-4)]" style={{ ...monoFont, fontSize: '10px' }}>
                  By status
                </span>
              </div>
              {cs.total === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <EmptyState
                    icon={UsersThree}
                    title="No contacts yet"
                    ctaLabel="Add contacts"
                    onCta={() => navigate('/contacts')}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* One hue, opacity ramp through a CSS variable — the same
                      mechanism EmailPerformanceFunnel uses, so both charts on
                      this row read as one system and both are dual-mode. This
                      was a third hardcoded 5-hex indigo ramp whose pale tail
                      (#bcc2ee, #d4d8f3) was tuned for light mode only. */}
                  {[
                    { label: 'New',         count: cs.by_status?.new || 0 },
                    { label: 'Contacted',   count: cs.by_status?.contacted || 0 },
                    { label: 'Engaged',     count: cs.by_status?.engaged || 0 },
                    { label: 'Qualified',   count: cs.by_status?.qualified || 0 },
                    { label: 'Unqualified', count: cs.by_status?.unqualified || 0 },
                  ].map((s, i, arr) => {
                    const op = 0.9 - (i / (arr.length - 1)) * 0.45
                    // A genuine zero gets no bar. The old `Math.max(…, 2)` floor
                    // drew a visible bar for a stage holding nobody.
                    const pctWidth =
                      cs.total > 0 && s.count > 0
                        ? Math.max((s.count / cs.total) * 100, 1.5)
                        : 0
                    return (
                      <div key={s.label} className="flex items-center gap-3">
                        <div className="flex items-center gap-2 w-28">
                          <span
                            className="h-1.5 w-1.5 rounded-full shrink-0"
                            style={{ background: 'var(--ed-accent)', opacity: op }}
                          />
                          <span
                            className="text-[var(--ed-text-3)]"
                            style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
                          >
                            {s.label}
                          </span>
                        </div>
                        <div
                          className="flex-1 h-[6px] rounded-full overflow-hidden"
                          style={{ background: 'var(--ed-bg-track)' }}
                        >
                          <motion.div
                            className="h-full rounded-full"
                            style={{ background: 'var(--ed-accent)', opacity: op }}
                            initial={{ width: 0 }}
                            animate={{ width: `${pctWidth}%` }}
                            transition={{ duration: 0.8, ease: easeOutExpo, delay: 0.15 }}
                          />
                        </div>
                        <span
                          className="text-[var(--ed-text-1)] w-14 text-right tabular-nums"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          {fmt(s.count)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </DarkCard>
          </div>
        </motion.section>

        {/* Intelligence — Recent Campaigns + AI Insights */}
        <motion.section variants={itemVariants}>
          <SectionLabel>Intelligence</SectionLabel>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
            {/* Recent Campaigns */}
            <DarkCard hoverable={false} className="p-5 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2
                  className="text-[var(--ed-text-1)]"
                  style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
                >
                  Recent Campaigns
                </h2>
                <button
                  onClick={() => navigate('/campaigns')}
                  className="group inline-flex items-center gap-1 text-[var(--ed-accent)] hover:text-[var(--ed-accent-hover)] transition-colors duration-150"
                  style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.13px' }}
                >
                  View all
                  <ArrowRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" weight="bold" />
                </button>
              </div>
              {!data?.recent_campaigns?.length ? (
                <div className="flex-1 flex items-center justify-center">
                  <EmptyState
                    icon={PaperPlaneTilt}
                    title="No campaigns yet"
                    ctaLabel="Launch campaign"
                    onCta={() => navigate('/campaigns')}
                  />
                </div>
              ) : (
                <>
                  {/* Aggregate strip — recent batch metrics */}
                  {(() => {
                    const list = data.recent_campaigns
                    const totalSent = list.reduce((s, c) => s + (c.emails_sent ?? 0), 0)
                    const totalRecipients = list.reduce((s, c) => s + (c.total_recipients ?? 0), 0)
                    // Volume-weighted open rate (sum opened / sum sent) across the
                    // batch — an unweighted mean of per-campaign rates let tiny
                    // campaigns distort the number away from every other open-rate
                    // metric on the platform.
                    const totalOpened = list.reduce((s, c) => s + (c.emails_opened ?? 0), 0)
                    const avgOpen = totalSent ? (totalOpened / totalSent) * 100 : 0
                    const aggStats = [
                      { label: 'Sent', value: fmt(totalSent), icon: PaperPlaneTilt },
                      { label: 'Reached', value: fmt(totalRecipients), icon: UsersThree },
                      { label: 'Avg open', value: `${avgOpen.toFixed(1)}%`, icon: TrendUp },
                    ]
                    return (
                      <div className="grid grid-cols-3 gap-3 mb-5">
                        {aggStats.map((s) => (
                          <div
                            key={s.label}
                            className="p-3 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)]"
                          >
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <s.icon
                                className="h-3.5 w-3.5 text-[var(--ed-text-4)]"
                                weight="regular"
                                aria-hidden="true"
                              />
                              <p
                                className="text-[var(--ed-text-4)]"
                                style={{ ...monoFont, fontSize: '10px', letterSpacing: '0.16em' }}
                              >
                                {s.label}
                              </p>
                            </div>
                            <p
                              className="text-[var(--ed-text-1)] tabular-nums"
                              style={{ fontSize: '22px', fontWeight: 590, letterSpacing: '-0.4px', lineHeight: 1.05 }}
                            >
                              {s.value}
                            </p>
                          </div>
                        ))}
                      </div>
                    )
                  })()}

                  <div className="flex-1 divide-y divide-[var(--ed-border-subtle)]">
                    {data.recent_campaigns.map((c) => {
                      const tint = getStatusTint(c.status)
                      return (
                        <motion.div
                          key={c.id}
                          onClick={() => navigate(`/campaigns/${c.id}`)}
                          className="flex items-center justify-between gap-4 py-3 px-2 -mx-2 rounded-md cursor-pointer hover:bg-[var(--ed-bg-track-soft)]"
                        >
                          <div className="min-w-0 flex-1">
                            <p
                              className="text-[var(--ed-text-1)] truncate"
                              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                            >
                              {c.name}
                            </p>
                            <div className="flex items-center gap-3 mt-1.5">
                              <span
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border"
                                style={{
                                  ...statusPillStyle,
                                  borderColor: 'var(--ed-border)',
                                  color: tint.text,
                                }}
                              >
                                <span
                                  className="h-1 w-1 rounded-full"
                                  style={{ background: tint.dot }}
                                />
                                {campaignStatusMeta(c.status).label}
                              </span>
                              <span
                                className="text-[var(--ed-text-4)]"
                                style={{ fontSize: '11px', letterSpacing: '-0.13px' }}
                              >
                                <span className="tabular-nums">{fmt(c.total_recipients ?? 0)}</span> recipients
                              </span>
                              {c.created_at && (
                                <span
                                  className="text-[var(--ed-text-4)]"
                                  style={{ fontSize: '11px', letterSpacing: '-0.13px' }}
                                >
                                  {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p
                              className="text-[var(--ed-text-1)] tabular-nums"
                              style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
                            >
                              {fmt(c.emails_sent ?? 0)}
                            </p>
                            <p
                              className="text-[var(--ed-text-4)]"
                              style={{ ...monoFont, fontSize: '9px', letterSpacing: '0.16em' }}
                            >
                              Sent
                            </p>
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>

                  {/* Footer CTA — pinned to card bottom so the card fills equal-height grid */}
                  <div className="mt-auto pt-5 border-t border-[var(--ed-border-subtle)]">
                    <div className="flex items-center justify-between gap-3 p-3 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)]">
                      <div className="min-w-0">
                        <p
                          className="text-[var(--ed-text-1)]"
                          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        >
                          Reach a new audience
                        </p>
                        <p
                          className="text-[var(--ed-text-3)] mt-0.5"
                          style={{ fontSize: '12px', lineHeight: 1.5, letterSpacing: '-0.13px' }}
                        >
                          Launch a campaign to your contacts or a new segment.
                        </p>
                      </div>
                      <motion.button
                        onClick={() => setShowLaunchModal(true)}
                        className="group shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[var(--ed-brand)] hover:bg-[var(--ed-accent-hover)] active:bg-[var(--ed-accent-pressed)] transition-colors duration-200 text-white"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <Rocket className="h-3.5 w-3.5" weight="regular" />
                        Launch
                      </motion.button>
                    </div>
                  </div>
                </>
              )}
            </DarkCard>

            {/* AI Insights */}
            <DarkCard hoverable={false} className="p-5 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2
                  className="text-[var(--ed-text-1)] flex items-center gap-2"
                  style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
                >
                  <Sparkle className="h-4 w-4 text-[var(--ed-accent)]" weight="regular" />
                  AI Insights
                </h2>
                {insights?.status === 'completed' && (
                  <div className="flex items-center gap-3">
                    {insights.generated_at && (
                      <span
                        className="text-[var(--ed-text-4)]"
                        style={{ fontSize: '11px', letterSpacing: '-0.13px' }}
                      >
                        {formatDistanceToNow(new Date(insights.generated_at), { addSuffix: true })}
                      </span>
                    )}
                    <button
                      onClick={handleGenerateInsights}
                      disabled={insightsGenerating}
                      aria-label="Regenerate insights"
                      className="h-7 w-7 rounded-md flex items-center justify-center text-[var(--ed-text-4)] hover:text-[var(--ed-text-1)] hover:bg-[var(--ed-bg-hover)] transition-colors duration-150 disabled:opacity-60"
                    >
                      <ArrowsClockwise
                        className={`h-4 w-4 ${insightsGenerating ? 'animate-spin' : ''}`}
                        weight="regular"
                      />
                    </button>
                  </div>
                )}
              </div>

              {(insightsLoading || insightsGenerating) && (
                <div className="flex flex-col items-center justify-center py-12">
                  <DotLoader size="md" className="text-[var(--ed-accent)]" />
                  <p
                    className="text-[var(--ed-text-4)] mt-5"
                    style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                  >
                    <TextShimmer>{insightsGenerating ? 'Analyzing materials…' : 'Loading…'}</TextShimmer>
                  </p>
                </div>
              )}

              {/* Danger, not amber. A failed request is an error, and §3 keeps
                  amber for warnings and trust signals — using it here left the
                  only two failure surfaces on the card looking like advisories. */}
              {insightsError && !insightsLoading && !insightsGenerating && (
                <div role="alert" className="flex flex-col items-center justify-center py-10">
                  <div className="h-11 w-11 rounded-md bg-[var(--ed-danger-soft)] border border-[var(--ed-danger-border)] flex items-center justify-center mb-4">
                    <WarningCircle className="h-5 w-5 text-[var(--ed-danger)]" weight="regular" />
                  </div>
                  <p
                    className="text-[var(--ed-text-2)] text-center max-w-sm"
                    style={{ fontSize: '13px', lineHeight: 1.5, letterSpacing: '-0.13px' }}
                  >
                    {insightsError}
                  </p>
                  <div className="mt-4">
                    {insightsError.toLowerCase().includes('api key') ? (
                      <SecondaryButton
                        icon={GearSix}
                        label="Go to Settings"
                        onClick={() => navigate('/settings')}
                      />
                    ) : (
                      <SecondaryButton
                        icon={ArrowsClockwise}
                        label="Try again"
                        onClick={handleGenerateInsights}
                      />
                    )}
                  </div>
                </div>
              )}

              {!insightsLoading &&
                !insightsGenerating &&
                !insightsError &&
                insights?.status === 'no_materials' && (
                  <EmptyState
                    icon={UploadSimple}
                    title="Upload materials to unlock AI insights"
                    ctaLabel="Upload materials"
                    onCta={() => navigate('/materials')}
                  />
                )}

              {/* `failed` used to be folded in with `not_generated`, so a
                  generation that errored server-side rendered the neutral
                  indigo "never run yet" invitation and the user was never told
                  the last attempt failed. Its own state, in danger tokens. */}
              {!insightsLoading &&
                !insightsGenerating &&
                !insightsError &&
                insights?.status === 'failed' && (
                  <div role="alert" className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="h-12 w-12 rounded-md bg-[var(--ed-danger-soft)] border border-[var(--ed-danger-border)] flex items-center justify-center mb-4">
                      <WarningCircle className="h-5 w-5 text-[var(--ed-danger)]" weight="regular" />
                    </div>
                    <p
                      className="text-[var(--ed-text-1)] mb-1.5"
                      style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      Last analysis failed
                    </p>
                    <p
                      className="text-[var(--ed-text-3)] max-w-sm mb-5"
                      style={{ fontSize: '12px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
                    >
                      Your materials were not analyzed. Retry the run, or check your
                      model settings if it keeps failing.
                    </p>
                    <SecondaryButton
                      icon={ArrowsClockwise}
                      label="Try again"
                      onClick={handleGenerateInsights}
                    />
                  </div>
                )}

              {!insightsLoading &&
                !insightsGenerating &&
                !insightsError &&
                insights?.status === 'not_generated' && (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="h-12 w-12 rounded-md bg-[var(--ed-accent-soft)] border border-[var(--ed-accent-border)] flex items-center justify-center mb-4">
                      <Sparkle className="h-5 w-5 text-[var(--ed-accent)]" weight="regular" />
                    </div>
                    <p
                      className="text-[var(--ed-text-1)] mb-1.5"
                      style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      Generate strategy insights
                    </p>
                    <p
                      className="text-[var(--ed-text-3)] max-w-sm mb-5"
                      style={{ fontSize: '12px', lineHeight: 1.6, letterSpacing: '-0.13px' }}
                    >
                      Analyze your {insights?.material_count || 0} material
                      {(insights?.material_count || 0) !== 1 ? 's' : ''} to surface marketing strategies,
                      target industries, and ideal customer profiles.
                    </p>
                    <PrimaryButton
                      icon={Sparkle}
                      label="Generate insights"
                      onClick={handleGenerateInsights}
                    />
                  </div>
                )}

              {!insightsLoading &&
                !insightsGenerating &&
                !insightsError &&
                insights?.status === 'completed' && (
                  <div className="space-y-4 flex-1 flex flex-col">
                    {/* Stale is a genuine warning, so amber is correct here — but
                        through the page's own `--ed-warn-*` tokens rather than six
                        raw amber-500 Tailwind utilities that ignore the palette. */}
                    {insights.is_stale && (
                      <div
                        role="status"
                        className="flex items-center gap-3 px-3.5 py-2.5 rounded-md bg-[var(--ed-warn-soft-1)] border border-[var(--ed-warn-border)]"
                      >
                        <Warning className="h-4 w-4 text-[var(--ed-warn-icon)] shrink-0" weight="fill" />
                        {/* Body copy is a text token, not the amber one: dark-mode
                            `--ed-warn-text` (#a3741f) lands at ~4.4:1 on this
                            surface, under AA for 12px. The amber lives in the icon
                            and border, which is where the house pattern puts it. */}
                        <p
                          className="text-[var(--ed-text-2)] flex-1"
                          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                        >
                          New materials since last analysis.
                        </p>
                        <button
                          onClick={handleGenerateInsights}
                          disabled={insightsGenerating}
                          className="text-[var(--ed-accent)] hover:text-[var(--ed-accent-hover)] disabled:opacity-50 transition-colors duration-150"
                          style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                        >
                          Regenerate
                        </button>
                      </div>
                    )}

                    {/* Hero Summary — sanitized so legacy raw-JSON blobs never render (21572) */}
                    {sanitizeInsightSummary(insights.executive_summary) && (
                      <div className="p-4 rounded-md bg-[var(--ed-accent-soft)] border border-[var(--ed-accent-border)]">
                        <div className="flex items-start gap-3">
                          <div className="h-7 w-7 rounded-md bg-[var(--ed-accent-soft)] border border-[var(--ed-accent-border)] flex items-center justify-center shrink-0">
                            <Sparkle className="h-3.5 w-3.5 text-[var(--ed-accent)]" weight="regular" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p
                              className="text-[var(--ed-text-2)]"
                              style={{ fontSize: '13px', lineHeight: 1.65, letterSpacing: '-0.13px' }}
                            >
                              {sanitizeInsightSummary(insights.executive_summary)}
                            </p>
                            <span
                              className="inline-block mt-2 px-2 py-0.5 rounded-full bg-[var(--ed-accent-soft)] text-[var(--ed-accent)] tabular-nums"
                              style={{ ...statusPillStyle, fontSize: '10px' }}
                            >
                              {insights.material_count ?? 0} materials analyzed
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Quick Stats Strip — §31 alignment (24px tabular-nums + mono eyebrow) */}
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { value: insights.marketing_strategies?.length || 0, label: 'Strategies', icon: Target },
                        { value: insights.target_industries?.length || 0, label: 'Industries', icon: Buildings },
                        { value: insights.campaign_suggestions?.length || 0, label: 'Campaigns', icon: PaperPlaneTilt },
                        { value: insights.additional_insights?.length || 0, label: 'Insights', icon: Lightbulb },
                      ].map((stat) => (
                        <div
                          key={stat.label}
                          className="p-3 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)]"
                        >
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <stat.icon
                              className="h-3.5 w-3.5 text-[var(--ed-text-4)]"
                              weight="regular"
                              aria-hidden="true"
                            />
                            <p
                              className="text-[var(--ed-text-4)]"
                              style={{ ...monoFont, fontSize: '10px', letterSpacing: '0.16em' }}
                            >
                              {stat.label}
                            </p>
                          </div>
                          <p
                            className="text-[var(--ed-text-1)] tabular-nums"
                            style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.4px', lineHeight: 1.05 }}
                          >
                            {stat.value}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Next Steps — top 3 numbered */}
                    {(insights.next_steps?.length ?? 0) > 0 && (
                      <div className="space-y-2">
                        <h4
                          className="text-[var(--ed-text-3)] flex items-center gap-1.5"
                          style={{ ...monoFont, fontSize: '10px' }}
                        >
                          <CheckCircle className="h-3 w-3 text-[var(--ed-success)]" weight="regular" />
                          Next steps
                        </h4>
                        {insights.next_steps!.slice(0, 3).map((step, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-2.5 p-2.5 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)] hover:border-[var(--ed-border-strong)] transition-colors duration-150"
                          >
                            <span
                              className="flex items-center justify-center h-5 w-5 rounded-full shrink-0 mt-0.5 text-white"
                              style={{
                                background: 'var(--ed-success)',
                                fontSize: '10px',
                                fontWeight: 510,
                              }}
                            >
                              {i + 1}
                            </span>
                            <p
                              className="text-[var(--ed-text-2)]"
                              style={{ fontSize: '12px', lineHeight: 1.55, letterSpacing: '-0.13px' }}
                            >
                              {step}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Top Strategies — 2 cards */}
                    {(insights.marketing_strategies?.length ?? 0) > 0 && (
                      <div className="space-y-2">
                        <h4
                          className="text-[var(--ed-text-3)] flex items-center gap-1.5"
                          style={{ ...monoFont, fontSize: '10px' }}
                        >
                          <Target className="h-3 w-3 text-[var(--ed-accent)]" weight="regular" />
                          Top strategies
                        </h4>
                        {insights.marketing_strategies!.slice(0, 2).map((s, i) => {
                          // Priority is recommendation strength, not error severity. Red (danger) would
                          // mis-read as "broken". Use amber for high (attention), indigo for medium
                          // (default accent), muted for low (background). §3, §11.
                          const priorityTint =
                            s.priority === 'high'
                              ? { bg: 'var(--ed-warn-soft-2)', text: 'var(--ed-warn-icon)', border: 'var(--ed-warn-border)' }
                              : s.priority === 'low'
                                ? { bg: 'var(--ed-bg-nested)', text: 'var(--ed-text-4)', border: 'var(--ed-border-subtle)' }
                                : { bg: 'var(--ed-accent-soft)', text: 'var(--ed-accent)', border: 'var(--ed-accent-border)' }
                          return (
                            <div
                              key={i}
                              className="relative p-3 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)] overflow-hidden"
                            >
                              <span
                                aria-hidden="true"
                                className="absolute left-0 top-0 bottom-0 w-px"
                                style={{
                                  background: `linear-gradient(180deg, ${priorityTint.text} 0%, color-mix(in srgb, ${priorityTint.text} 20%, transparent) 45%, transparent 100%)`,
                                }}
                              />
                              <div className="flex items-start justify-between gap-3 mb-1">
                                <p
                                  className="text-[var(--ed-text-1)]"
                                  style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                                >
                                  {s.title}
                                </p>
                                <span
                                  className="shrink-0 px-2 py-0.5 rounded-full"
                                  style={{
                                    ...statusPillStyle,
                                    background: priorityTint.bg,
                                    color: priorityTint.text,
                                  }}
                                >
                                  {s.priority}
                                </span>
                              </div>
                              <p
                                className="text-[var(--ed-text-3)]"
                                style={{ fontSize: '12px', lineHeight: 1.5, letterSpacing: '-0.13px' }}
                              >
                                {s.description}
                              </p>
                              {s.estimated_impact && (
                                <p
                                  className="text-[var(--ed-accent)] mt-2 flex items-center gap-1"
                                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.13px' }}
                                >
                                  <TrendUp className="h-3 w-3" weight="regular" />
                                  {s.estimated_impact}
                                </p>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* View Full Insights — pinned to bottom for symmetric card height with Recent Campaigns */}
                    <motion.button
                      onClick={() => setShowInsightsModal(true)}
                      className="group w-full flex items-center justify-center gap-2 h-10 rounded-md bg-[var(--ed-accent-soft)] border border-[var(--ed-accent-border)] text-[var(--ed-accent)] hover:bg-[var(--ed-accent-border)] mt-auto"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      whileTap={{ scale: 0.98 }}
                    >
                      View full insights
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" weight="bold" />
                    </motion.button>

                  </div>
                )}
            </DarkCard>
          </div>
        </motion.section>

        {/* Resources */}
        <motion.section variants={itemVariants}>
          <SectionLabel>Resources</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CompactStat
              icon={TreeStructure}
              count={fmt(o.total_workflows)}
              label="Workflows"
              meta={`${fmt(o.active_workflows)} active`}
              onClick={() => navigate('/workflows')}
            />
            <CompactStat
              icon={FileText}
              count={fmt(o.total_materials)}
              label="Materials"
              meta="uploaded"
              onClick={() => navigate('/materials')}
            />
          </div>
        </motion.section>
        </>
        )}

        {/* Profile completion — demoted to the bottom once the account is active */}
        {onboarding && (cardMode === 'progress' || cardMode === 'complete') && (
          <motion.section variants={itemVariants}>
            <SectionLabel>Setup</SectionLabel>
            <ProfileCompletionCard
              status={onboarding}
              mode={cardMode}
              onGo={(route) => navigate(route)}
              onDismiss={() => {
                dismissProfileCard(user?.id)
                setProfileDismissed(true)
              }}
            />
          </motion.section>
        )}
      </motion.div>

      <ContentInsightsModal
        isOpen={showInsightsModal}
        onClose={() => setShowInsightsModal(false)}
      />

      {/* New Campaign modal — opens in place; a successful Launch redirects to the campaign list. */}
      <LaunchCampaignModal
        isOpen={showLaunchModal}
        onClose={() => setShowLaunchModal(false)}
        onSuccess={() => {
          setShowLaunchModal(false)
          if (onboarding && onboarding.campaigns_launched === 0) {
            toast.success('Your first campaign is launched. Nice work!')
          }
          loadOnboarding()
          navigate('/campaigns')
        }}
      />
    </div>
  )
}

// ---------- Subcomponents ----------

type IconType = PhosphorIconType

const KpiCard: React.FC<{
  label: string
  value: string
  meta: string
  icon: IconType
  onClick: () => void
}> = ({ label, value, meta, icon: Icon, onClick }) => (
  <DarkCard onClick={onClick} className="p-5">
    <div className="flex items-start justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-[var(--ed-text-4)]" style={{ ...monoFont, fontSize: '10px' }}>
          {label}
        </p>
        <p
          className="text-[var(--ed-text-1)] mt-2 tabular-nums"
          style={{ fontSize: '30px', fontWeight: 510, letterSpacing: '-0.7px', lineHeight: 1.05 }}
        >
          {value}
        </p>
        <p
          className="text-[var(--ed-text-4)] mt-1.5 tabular-nums"
          style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
        >
          {meta}
        </p>
      </div>
      <div className="h-9 w-9 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)] flex items-center justify-center shrink-0">
        <Icon className="h-[18px] w-[18px] text-[var(--ed-accent)]" weight="regular" />
      </div>
    </div>
  </DarkCard>
)

const CreditCard: React.FC<{
  remaining?: number
  remainingPct: number
  hasAllowance: boolean
  loaded: boolean
  severity: 'critical' | 'warn' | 'ok'
  /** Omitted for a role with no Billing page to land on. DarkCard drops its
   *  pointer cursor and hover state when there is nothing to click. */
  onClick?: () => void
}> = ({ remaining, remainingPct, hasAllowance, loaded, severity, onClick }) => {
  const fillColor =
    severity === 'critical' ? 'var(--ed-danger)' : severity === 'warn' ? 'var(--ed-warn-icon)' : 'var(--ed-brand)'
  const iconTint =
    severity === 'critical' ? 'var(--ed-danger)' : severity === 'warn' ? 'var(--ed-warn-icon)' : 'var(--ed-accent)'
  const iconBg =
    severity === 'critical'
      ? 'var(--ed-danger-soft)'
      : severity === 'warn'
        ? 'var(--ed-warn-soft-2)'
        : 'var(--ed-bg-hover)'
  const iconBorder =
    severity === 'critical'
      ? 'var(--ed-danger-border)'
      : severity === 'warn'
        ? 'var(--ed-warn-border)'
        : 'var(--ed-border-subtle)'

  return (
    <DarkCard onClick={onClick} className="p-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[var(--ed-text-4)]" style={{ ...monoFont, fontSize: '10px' }}>
            Credit Balance
          </p>
          {remaining !== undefined ? (
            <p
              className="text-[var(--ed-text-1)] mt-2 tabular-nums"
              style={{ fontSize: '30px', fontWeight: 510, letterSpacing: '-0.7px', lineHeight: 1.05 }}
            >
              {remaining.toLocaleString()}
            </p>
          ) : loaded ? (
            <p
              className="text-[var(--ed-text-1)] mt-2 tabular-nums"
              style={{ fontSize: '30px', fontWeight: 510, letterSpacing: '-0.7px', lineHeight: 1.05 }}
            >
              0
            </p>
          ) : (
            <Skeleton variant="text" width="96px" height="30px" className="mt-2.5" />
          )}
          <p
            className="text-[var(--ed-text-4)] mt-1.5 tabular-nums"
            style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
          >
            {/* An account with no allowance at all is "no plan", not "100%
                remaining" — the percentage is only meaningful against a
                non-zero allowance. */}
            {remaining !== undefined
              ? hasAllowance
                ? `${remainingPct}% remaining`
                : 'No credit allowance'
              : loaded
                ? 'No active balance'
                : <TextShimmer>Loading…</TextShimmer>}
          </p>
        </div>
        <div
          className="h-9 w-9 rounded-md flex items-center justify-center shrink-0 border"
          style={{ background: iconBg, borderColor: iconBorder }}
        >
          <Lightning className="h-[18px] w-[18px]" style={{ color: iconTint }} weight="regular" />
        </div>
      </div>
      {remaining !== undefined && hasAllowance && (
        <div
          className="mt-4 h-[3px] rounded-full overflow-hidden"
          style={{ background: 'var(--ed-bg-track)' }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ background: fillColor }}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(remainingPct, 100)}%` }}
            transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.2 }}
          />
        </div>
      )}
    </DarkCard>
  )
}

const CompactStat: React.FC<{
  icon: IconType
  count: string
  label: string
  meta: string
  onClick: () => void
}> = ({ icon: Icon, count, label, meta, onClick }) => (
  <DarkCard onClick={onClick} className="p-4">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)] flex items-center justify-center shrink-0">
        <Icon className="h-[18px] w-[18px] text-[var(--ed-accent)]" weight="regular" />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-[var(--ed-text-1)]"
          style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
        >
          <span className="tabular-nums">{count}</span> {label}
        </p>
        <p
          className="text-[var(--ed-text-4)] tabular-nums"
          style={{ fontSize: '12px', letterSpacing: '-0.13px' }}
        >
          {meta}
        </p>
      </div>
      <ArrowRight className="h-4 w-4 text-[var(--ed-text-5)]" weight="regular" />
    </div>
  </DarkCard>
)

const EmptyState: React.FC<{
  icon: IconType
  title: string
  ctaLabel: string
  onCta: () => void
}> = ({ icon: Icon, title, ctaLabel, onCta }) => (
  <div className="flex flex-col items-center justify-center py-10 text-center">
    <div className="h-11 w-11 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)] flex items-center justify-center mb-3.5">
      <Icon className="h-5 w-5 text-[var(--ed-text-4)]" weight="regular" />
    </div>
    <p
      className="text-[var(--ed-text-3)] mb-4"
      style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
    >
      {title}
    </p>
    <SecondaryButton label={ctaLabel} onClick={onCta} />
  </div>
)

type IconWeight = 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'

const PrimaryButton: React.FC<{
  icon?: IconType
  iconWeight?: IconWeight
  label: string
  onClick: () => void
}> = ({ icon: Icon, iconWeight = 'regular', label, onClick }) => (
  <motion.button
    onClick={onClick}
    className="relative overflow-hidden inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[var(--ed-brand)] hover:bg-[var(--ed-accent-hover)] active:bg-[var(--ed-accent-pressed)] transition-colors duration-200 text-white"
    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
    initial="rest"
    animate="rest"
    whileHover="sweep"
    whileFocus="sweep"
    whileTap={{ scale: 0.97 }}
  >
    <ButtonSheen />
    {Icon && <Icon className="relative h-4 w-4" weight={iconWeight} />}
    <span className="relative">{label}</span>
  </motion.button>
)

const SecondaryButton: React.FC<{
  icon?: IconType
  iconWeight?: IconWeight
  label: string
  onClick: () => void
}> = ({ icon: Icon, iconWeight = 'regular', label, onClick }) => (
  <motion.button
    onClick={onClick}
    className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[var(--ed-bg-idle)] border border-[var(--ed-border)] text-[var(--ed-text-2)] hover:bg-[var(--ed-bg-hover)] hover:border-[var(--ed-border-strong)]"
    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
    whileTap={{ scale: 0.98 }}
  >
    {Icon && <Icon className="h-4 w-4 text-[var(--ed-text-3)]" weight={iconWeight} />}
    {label}
  </motion.button>
)

export default DashboardPage
